import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Check,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  Clock,
  FileText,
  Image as ImageIcon,
  LayoutGrid,
  List,
  Loader2,
  Mail,
  MapPin,
  Pencil,
  Plus,
  RefreshCw,
  Rocket,
  Search,
  Sparkles,
  Tag,
  Trash2,
  Upload,
  UserCheck,
  Users,
  X,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { EmptyPanel, PagePanel, PageShell, PanelHeading } from "@/components/interior-page";
import Loader from "@/components/ui/loader";
import { Pagination } from "@/components/ui/pagination";
import { useToast } from "@/components/ui/toast";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { isOrgAdmin } from "@/lib/roles";

interface LeadPipeline {
  id: string;
  /** Organization that created the lead — only its admins may change it. */
  org_id?: number;
  reference_code: string | null;
  image_url: string | null;
  title: string | null;
  company_name: string | null;
  owner_email: string | null;
  website_url: string | null;
  city_raw: string | null;
  state_raw: string | null;
  business_description: string | null;
  industry_category_id: number | null;
  industry_category: string | null;
  location: string | null;
  potential_category: string | null;
  interest_count: number;
  created_at: string;
}

interface AdvisorInfo { id: string; first_name: string; last_name: string; email: string; }
interface BuyerInfo { id: string; first_name: string; last_name: string; email: string; phone: string | null; }
interface AssignmentHistoryEntry { id: number; advisor_id: string; advisor_name: string; action: string; note: string | null; created_at: string; }

interface LeadInterest {
  id: number;
  pipeline_id: string;
  pipeline_reference_code: string | null;
  pipeline_title: string;
  buyer: BuyerInfo;
  assigned_advisor: AdvisorInfo | null;
  assignment_status: string;
  buyer_message: string | null;
  assigned_at: string | null;
  responded_at: string | null;
  created_at: string;
  assignment_history: AssignmentHistoryEntry[];
}

interface PagedLeads { total_elements: number; total_pages: number; page: number; page_size: number; data: LeadPipeline[]; }
interface PagedInterests { total_elements: number; total_pages: number; page: number; page_size: number; data: LeadInterest[]; }

interface Advisor { id: string; first_name: string; last_name: string; }
interface LookupItem { id: number; name: string; }

const STATUS_COLOR: Record<string, string> = {
  pending: "bg-amber-500/15 text-amber-600",
  accepted: "bg-green-500/15 text-green-600",
  rejected: "bg-red-500/15 text-red-600",
};

const EMPTY_FORM = {
  title: "", company_name: "", owner_email: "", website_url: "",
  city_raw: "", state_raw: "", business_description: "",
  industry_category_id: "", potential_category: "",
};

const PAGE_SIZE = 20;

/* The Leads tab presents the same two views as the buyer-facing Coming Soon tab
   (ComingSoonDeals) — card grid and table — so staff and buyers are looking at
   the same deals in the same shape. Presentation only: the fetch, search,
   pagination and the per-row admin actions are untouched. */

const leadTitle = (l: LeadPipeline) => l.title || l.company_name || "Untitled lead";

const ComingSoonBadge = () => (
  <span className="inline-flex items-center gap-1 rounded-full bg-[linear-gradient(135deg,hsl(43_74%_60%),hsl(38_64%_46%))] px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-[0.12em] text-[hsl(40_45%_12%)] shadow-sm whitespace-nowrap">
    <Sparkles className="h-3 w-3" />
    Coming Soon
  </span>
);

export default function AdminLeadPipelinePage() {
  const { user } = useAuth();
  // An org_admin sees every organization's leads (the buyer tab does too) but
  // may only change their own, and the Settings tab is platform-wide config.
  const orgAdmin = isOrgAdmin(user);
  const canEditLead = (l: LeadPipeline) => !orgAdmin || l.org_id === user?.org_id;
  const [leads, setLeads] = useState<LeadPipeline[]>([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsSearch, setLeadsSearch] = useState("");
  const [leadsSearchInput, setLeadsSearchInput] = useState("");
  const [leadsLoading, setLeadsLoading] = useState(true);
  // Grid is the default here too, matching the buyer marketplace.
  const [leadsView, setLeadsView] = useState<"grid" | "table">("grid");

  const [interests, setInterests] = useState<LeadInterest[]>([]);
  const [interestsTotal, setInterestsTotal] = useState(0);
  const [interestsPage, setInterestsPage] = useState(1);
  const [interestsSearch, setInterestsSearch] = useState("");
  const [interestsSearchInput, setInterestsSearchInput] = useState("");
  const [interestsLoading, setInterestsLoading] = useState(true);

  const [advisors, setAdvisors] = useState<Advisor[]>([]);
  const [categories, setCategories] = useState<LookupItem[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);

  const [activeTab, setActiveTab] = useState<"leads" | "interests" | "config">("leads");
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [editingLead, setEditingLead] = useState<LeadPipeline | null>(null);
  const [expandedInterest, setExpandedInterest] = useState<number | null>(null);
  const [reassignModal, setReassignModal] = useState<{ interestId: number; currentAdvisor: string | null } | null>(null);
  const [reassignAdvisorId, setReassignAdvisorId] = useState("");
  const [reassignNote, setReassignNote] = useState("");
  const [advisorSearch, setAdvisorSearch] = useState("");
  const [advisorDropdownOpen, setAdvisorDropdownOpen] = useState(false);
  const [configValue, setConfigValue] = useState("3");
  const [savingConfig, setSavingConfig] = useState(false);
  const toast = useToast();

  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [generatingId, setGeneratingId] = useState<string | null>(null);

  // Lead detail modal (opened from interests tab)
  const navigate = useNavigate();
  const [viewLead, setViewLead] = useState<LeadPipeline | null>(null);
  const [viewLeadLoading, setViewLeadLoading] = useState(false);
  const [viewLeadMode, setViewLeadMode] = useState<"detail" | "publish">("detail");
  const [publishChecklist, setPublishChecklist] = useState<{
    owner_connected: boolean | null;
    due_diligence_completed: boolean | null;
    nda_signed: boolean | null;
  }>({ owner_connected: null, due_diligence_completed: null, nda_signed: null });
  const [publishNotes, setPublishNotes] = useState("");
  const allPublishYes =
    publishChecklist.owner_connected === true &&
    publishChecklist.due_diligence_completed === true &&
    publishChecklist.nda_signed === true;

  const openViewLead = async (pipeline_id: string) => {
    setViewLeadMode("detail");
    setViewLead(null);
    setViewLeadLoading(true);
    // Try to find in already-loaded leads list first for instant display
    const cached = leads.find((l) => l.id === pipeline_id);
    if (cached) {
      setViewLead(cached);
      setViewLeadLoading(false);
    } else {
      try {
        const data = await api.get<LeadPipeline>(`/lead-pipeline/${pipeline_id}`);
        setViewLead(data);
      } catch { /* silent */ } finally { setViewLeadLoading(false); }
    }
  };

  const closeViewLead = () => { setViewLead(null); setViewLeadMode("detail"); };

  const confirmPublishFromView = () => {
    if (!viewLead || !allPublishYes) return;
    navigate("/listings/new", {
      state: {
        leadPrefill: viewLead,
        advisorChecklist: {
          owner_connected: true,
          due_diligence_completed: true,
          nda_signed: true,
          notes: publishNotes.trim() || null,
        },
      },
    });
    closeViewLead();
  };

  const loadLeads = useCallback(async (page: number, search: string) => {
    setLeadsLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
      if (search.trim()) params.set("search", search.trim());
      const res = await api.get<PagedLeads>(`/lead-pipeline/admin/leads?${params}`);
      setLeads(res.data);
      setLeadsTotal(res.total_elements);
    } catch { /* silent */ } finally { setLeadsLoading(false); }
  }, []);

  const loadInterests = useCallback(async (page: number, search: string) => {
    setInterestsLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
      if (search.trim()) params.set("search", search.trim());
      const res = await api.get<PagedInterests>(`/lead-pipeline/admin/interests?${params}`);
      setInterests(res.data);
      setInterestsTotal(res.total_elements);
    } catch { /* silent */ } finally { setInterestsLoading(false); }
  }, []);

  useEffect(() => {
    Promise.all([
      api.get<Advisor[]>("/admin/advisors"),
      orgAdmin
        ? Promise.resolve({ value: "3" })
        : api.get<{ value: string }>("/lead-pipeline/admin/config/advisor_active_days_threshold").catch(() => ({ value: "3" })),
      api.get<LookupItem[]>("/lookups/categories").catch(() => []),
    ]).then(([a, cfg, cats]) => {
      setAdvisors(a);
      setConfigValue(cfg.value);
      setCategories(cats);
    }).catch(() => {}).finally(() => setInitialLoading(false));
  }, []);

  useEffect(() => { loadLeads(leadsPage, leadsSearch); }, [leadsPage, leadsSearch, loadLeads]);
  useEffect(() => { loadInterests(interestsPage, interestsSearch); }, [interestsPage, interestsSearch, loadInterests]);

  const submitLeadsSearch = () => {
    setLeadsPage(1);
    setLeadsSearch(leadsSearchInput);
  };

  const submitInterestsSearch = () => {
    setInterestsPage(1);
    setInterestsSearch(interestsSearchInput);
  };

  const openCreate = () => {
    setEditingLead(null);
    setForm({ ...EMPTY_FORM });
    setShowLeadForm(true);
  };

  const openEdit = (l: LeadPipeline) => {
    setEditingLead(l);
    setForm({
      title: l.title ?? "",
      company_name: l.company_name ?? "",
      owner_email: l.owner_email ?? "",
      website_url: l.website_url ?? "",
      city_raw: l.city_raw ?? "",
      state_raw: l.state_raw ?? "",
      business_description: l.business_description ?? "",
      industry_category_id: l.industry_category_id != null ? String(l.industry_category_id) : "",
      potential_category: l.potential_category ?? "",
    });
    setShowLeadForm(true);
  };

  const handleSaveLead = async () => {
    if (!form.title.trim() && !form.company_name.trim()) {
      toast.error("Title or company name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim() || null,
        company_name: form.company_name.trim() || null,
        owner_email: form.owner_email.trim() || null,
        website_url: form.website_url.trim() || null,
        city_raw: form.city_raw.trim() || null,
        state_raw: form.state_raw.trim() || null,
        business_description: form.business_description.trim() || null,
        industry_category_id: form.industry_category_id ? Number(form.industry_category_id) : null,
        potential_category: form.potential_category.trim() || null,
      };
      if (editingLead) {
        await api.put(`/lead-pipeline/admin/leads/${editingLead.id}`, payload);
        toast.success("Lead updated");
      } else {
        await api.post("/lead-pipeline/admin/leads", payload);
        toast.success("Lead created");
      }
      setShowLeadForm(false);
      loadLeads(leadsPage, leadsSearch);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to save");
    } finally { setSaving(false); }
  };

  const handleGenerateImage = async (id: string) => {
    setGeneratingId(id);
    try {
      const updated = await api.post<LeadPipeline>(`/lead-pipeline/admin/leads/${id}/generate-image`, {});
      setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, image_url: updated.image_url } : l)));
      toast.success("Image generated");
    } catch (e: any) {
      toast.error(e?.status === 503 ? "Image generation is not configured." : "Failed to generate image");
    } finally {
      setGeneratingId(null);
    }
  };

  /* Same cover treatment as the buyer cards: dark gold-tinted placeholder with a
     hatch pattern, and a scrim over real photos so the overlaid chips stay
     readable. Spinner while this lead's image is being generated. */
  const leadCover = (l: LeadPipeline, heightClass: string, iconClass: string) => (
    <div className={`relative ${heightClass} w-full shrink-0 overflow-hidden bg-[linear-gradient(135deg,hsl(40_20%_14%),hsl(40_16%_9%))]`}>
      {l.image_url ? (
        <>
          <img src={l.image_url} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-black/25" />
        </>
      ) : generatingId === l.id ? (
        <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-white/50">
          <Loader2 className="h-7 w-7 animate-spin" />
          <span className="text-[0.65rem] font-semibold uppercase tracking-wide">Generating image…</span>
        </div>
      ) : (
        <>
          <div
            className="pointer-events-none absolute inset-0 opacity-40"
            style={{ backgroundImage: "repeating-linear-gradient(135deg, hsl(43 30% 60% / 0.08) 0 10px, transparent 10px 20px)" }}
          />
          <div className="relative flex h-full w-full items-center justify-center">
            <Building2 className={`${iconClass} text-white/25`} />
          </div>
        </>
      )}
    </div>
  );

  const handleDeleteLead = async (id: string) => {
    if (!confirm("Delete this lead? This cannot be undone.")) return;
    try {
      await api.delete(`/lead-pipeline/admin/leads/${id}`);
      toast.success("Deleted");
      loadLeads(leadsPage, leadsSearch);
    } catch { toast.error("Failed to delete"); }
  };

  const handleReassign = async () => {
    if (!reassignModal || !reassignAdvisorId) return;
    try {
      await api.put(`/lead-pipeline/admin/interests/${reassignModal.interestId}/reassign`, {
        advisor_id: reassignAdvisorId,
        note: reassignNote.trim() || null,
      });
      toast.success("Reassigned");
      setReassignModal(null);
      setReassignAdvisorId("");
      setReassignNote("");
      loadInterests(interestsPage, interestsSearch);
    } catch { toast.error("Failed to reassign"); }
  };

  const handleSaveConfig = async () => {
    if (!configValue || isNaN(Number(configValue)) || Number(configValue) < 1) {
      toast.error("Enter a valid number of days (≥1)");
      return;
    }
    setSavingConfig(true);
    try {
      await api.put("/lead-pipeline/admin/config/advisor_active_days_threshold", { value: configValue });
      toast.success("Threshold updated");
    } catch { toast.error("Failed to update"); } finally { setSavingConfig(false); }
  };

  if (initialLoading && leadsLoading) return <Loader />;

  return (
    <PageShell>
      {/* Hero — matches the Coming Soon page style */}
      <section className="px-0">
        <div className="public-hero-shell mx-auto rounded-[1.85rem] px-6 py-8 shadow-glow sm:px-8 sm:py-9">
          <div className="public-grid-lattice absolute inset-0 opacity-45" />
          <div className="absolute -right-14 top-0 h-40 w-40 rounded-full bg-white/30 blur-3xl" />
          <div className="relative z-10 grid gap-6 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div>
              <div className="public-pill">
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                Deal Pipeline
              </div>
              <h1 className="mt-4 font-display text-3xl leading-tight text-foreground sm:text-4xl lg:text-5xl">
                Coming Soon <span className="text-gradient">Leads</span>
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-muted-foreground sm:text-base">
                Manage the lead pipeline, track buyer interests, and oversee advisor assignments — all in one place.
              </p>
              <div className="mt-5 flex flex-wrap gap-2.5">
                <span className="public-pill border-primary/25 bg-white/70 text-primary">
                  <Building2 className="h-3.5 w-3.5" />
                  Off-Market Leads
                </span>
                <span className="public-pill bg-white/72">
                  <Users className="h-3.5 w-3.5" />
                  Buyer Interests
                </span>
                <span className="public-pill bg-white/72">
                  <UserCheck className="h-3.5 w-3.5" />
                  Advisor Routing
                </span>
              </div>
            </div>

            {/* Snapshot card */}
            <div className="public-glass-card rounded-[1.35rem] p-5">
              <h2 className="text-lg font-bold text-foreground">Pipeline snapshot</h2>
              <div className="mt-3 grid grid-cols-3 gap-2.5">
                <div className="public-glass-card rounded-xl p-3">
                  <div className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Leads</div>
                  <div className="mt-1.5 text-xl font-extrabold text-foreground">{leadsTotal.toLocaleString()}</div>
                </div>
                <div className="public-glass-card rounded-xl p-3">
                  <div className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Interests</div>
                  <div className="mt-1.5 text-xl font-extrabold text-foreground">{interestsTotal.toLocaleString()}</div>
                </div>
                <div className="public-glass-card rounded-xl p-3">
                  <div className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Advisors</div>
                  <div className="mt-1.5 text-xl font-extrabold text-foreground">{advisors.length || "--"}</div>
                </div>
              </div>
              <div className="mt-4 rounded-xl border border-primary/10 bg-white/60 p-4">
                <p className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">How it works</p>
                <ul className="mt-2 space-y-2">
                  {[
                    { icon: Building2, text: "Add or import off-market leads" },
                    { icon: Users, text: "Buyers express interest" },
                    { icon: UserCheck, text: "Auto-assigned to advisors round-robin" },
                  ].map(({ icon: Icon, text }) => (
                    <li key={text} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon className="h-3.5 w-3.5 shrink-0 text-primary/70" />
                      {text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="inline-flex flex-wrap gap-1 rounded-xl border border-border bg-muted/40 p-1">
        {(orgAdmin ? (["leads", "interests"] as const) : (["leads", "interests", "config"] as const)).map((t) => (
          <button
            key={t}
            onClick={() => setActiveTab(t)}
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
              activeTab === t
                ? "bg-white text-primary shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t === "leads" ? `Leads (${leadsTotal.toLocaleString()})` : t === "interests" ? `Interests (${interestsTotal.toLocaleString()})` : "Settings"}
          </button>
        ))}
      </div>

      {/* ── Leads Tab ── */}
      {activeTab === "leads" && (
        <PagePanel>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <PanelHeading title="All Coming Soon Leads" />
            <div className="flex items-center gap-2">
              {/* Grid / table toggle — same control as the buyer marketplace. */}
              <div className="flex items-center gap-1 rounded-xl border border-border/80 bg-card p-1">
                {([
                  { mode: "grid", icon: LayoutGrid, label: "Grid" },
                  { mode: "table", icon: List, label: "Table" },
                ] as const).map(({ mode, icon: Icon, label }) => (
                  <button
                    key={mode}
                    onClick={() => setLeadsView(mode)}
                    title={`${label} view`}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                      leadsView === mode
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-muted-foreground hover:bg-accent hover:text-foreground",
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="hidden sm:inline">{label}</span>
                  </button>
                ))}
              </div>
              <Button size="sm" onClick={openCreate}>
                <Plus className="h-4 w-4 mr-1" /> Add Lead
              </Button>
            </div>
          </div>

          {/* Search */}
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <input
                className="w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm"
                placeholder="Search by title, company, or email…"
                value={leadsSearchInput}
                onChange={(e) => setLeadsSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitLeadsSearch()}
              />
            </div>
            <Button size="sm" variant="outline" onClick={submitLeadsSearch}>Search</Button>
            {leadsSearch && (
              <Button size="sm" variant="ghost" onClick={() => { setLeadsSearchInput(""); setLeadsSearch(""); setLeadsPage(1); }}>
                <X className="h-4 w-4 mr-1" /> Clear
              </Button>
            )}
          </div>

          {leadsLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : leads.length === 0 ? (
            <EmptyPanel icon={Building2} title="No leads yet" description={leadsSearch ? "No leads match your search." : "Add a lead manually, or import them into the lead_pipeline table."} />
          ) : (
            <>
              {leadsView === "grid" ? (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {leads.map((l) => (
                    <div
                      key={l.id}
                      className="hover-lift public-listing-card group flex flex-col overflow-hidden rounded-2xl border border-border/70 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
                    >
                      <div className="relative">
                        {leadCover(l, "h-44", "h-12 w-12")}
                        {(l.industry_category || l.potential_category) && (
                          <div className="absolute left-3 top-3 flex flex-col items-start gap-1">
                            {/* Dark glass over the photo in BOTH themes, same as
                                the buyer cards — so white, not foreground. */}
                            {l.industry_category && (
                              <span className="inline-flex items-center rounded-md border border-primary/25 bg-black/60 px-2.5 py-1 text-[0.6rem] font-bold uppercase tracking-[0.12em] text-white/90 backdrop-blur-sm">
                                {l.industry_category}
                              </span>
                            )}
                            {l.potential_category && (
                              <span className="inline-flex items-center rounded-md border border-violet-300/30 bg-black/60 px-2.5 py-1 text-[0.6rem] font-bold uppercase tracking-[0.12em] text-violet-200 backdrop-blur-sm">
                                {l.potential_category}
                              </span>
                            )}
                          </div>
                        )}
                        <div className="absolute right-3 top-3">
                          <ComingSoonBadge />
                        </div>
                      </div>

                      <div className="flex flex-1 flex-col gap-3 p-4">
                        <div>
                          {/* Reference code above the title, the row holding its
                              height when a lead has none, so titles line up. */}
                          <div className="mb-1 flex h-4 items-center justify-between gap-2">
                            {l.reference_code ? (
                              <span className="font-mono text-xs font-bold tracking-wider text-primary">
                                {l.reference_code}
                              </span>
                            ) : (
                              <span />
                            )}
                            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary/5 px-2 py-0.5 text-[0.7rem] font-medium text-primary">
                              <Users className="h-3 w-3" /> {l.interest_count} interest{l.interest_count !== 1 ? "s" : ""}
                            </span>
                          </div>
                          <div className="min-h-[4.15rem]">
                            <h3 className="line-clamp-2 font-display text-xl font-bold leading-snug text-foreground">
                              {leadTitle(l)}
                            </h3>
                          </div>
                          {l.business_description && (
                            <p className="mt-1 line-clamp-3 text-xs leading-5 text-muted-foreground">{l.business_description}</p>
                          )}
                        </div>

                        <div className="flex flex-col gap-1.5 text-xs text-muted-foreground">
                          {l.company_name && (
                            <span className="flex items-center gap-1.5">
                              <Building2 className="h-3.5 w-3.5 shrink-0 text-primary/70" />
                              <span className="truncate" title={l.company_name}>{l.company_name}</span>
                            </span>
                          )}
                          {l.location && (
                            <span className="flex items-center gap-1.5">
                              <MapPin className="h-3.5 w-3.5 shrink-0 text-primary/70" />
                              <span className="truncate" title={l.location}>{l.location}</span>
                            </span>
                          )}
                          {l.owner_email && (
                            <span className="flex items-center gap-1.5">
                              <Mail className="h-3.5 w-3.5 shrink-0 text-primary/70" />
                              <span className="truncate" title={l.owner_email}>{l.owner_email}</span>
                            </span>
                          )}
                        </div>

                        {/* Admin actions, in the buyer cards' full-width button row. */}
                        {!canEditLead(l) ? (
                          <p className="mt-auto pt-1 text-center text-xs text-muted-foreground">Managed by another organization</p>
                        ) : (
                        <div className="mt-auto grid grid-cols-3 gap-2 pt-1">
                          <button
                            className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-primary/60 bg-transparent py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/10 disabled:opacity-50"
                            title={l.image_url ? "Regenerate image" : "Generate image"}
                            disabled={generatingId === l.id}
                            onClick={() => handleGenerateImage(l.id)}
                          >
                            {generatingId === l.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : l.image_url ? (
                              <ImageIcon className="h-4 w-4" />
                            ) : (
                              <Sparkles className="h-4 w-4" />
                            )}
                            Image
                          </button>
                          <button
                            className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-primary/60 bg-gold-gradient py-2.5 text-sm font-semibold text-primary-foreground transition-[filter] hover:brightness-[1.06]"
                            title="Edit lead"
                            onClick={() => openEdit(l)}
                          >
                            <Pencil className="h-4 w-4" />
                            Edit
                          </button>
                          <button
                            className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-destructive/40 bg-transparent py-2.5 text-sm font-semibold text-destructive transition-colors hover:bg-destructive/10"
                            title="Delete lead"
                            onClick={() => handleDeleteLead(l.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </button>
                        </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border/70 bg-muted/40">
                          {["BL Code", "Category", "Title", "Location", "Interests", "Status"].map((h) => (
                            <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                              {h}
                            </th>
                          ))}
                          <th className="px-4 py-3" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border/50">
                        {leads.map((l) => (
                          <tr key={l.id} className="group transition-colors hover:bg-muted/30">
                            <td className="whitespace-nowrap px-4 py-3.5">
                              {l.reference_code ? (
                                <span className="font-mono text-xs font-bold tracking-wider text-primary">
                                  {l.reference_code}
                                </span>
                              ) : (
                                <span className="text-muted-foreground/50">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3.5">
                              {l.industry_category ? (
                                <span className="inline-flex items-center whitespace-nowrap rounded-full border border-primary/15 bg-primary/10 px-2 py-0.5 text-[0.64rem] font-semibold uppercase tracking-[0.1em] text-primary">
                                  {l.industry_category}
                                </span>
                              ) : (
                                <span className="text-muted-foreground/50">—</span>
                              )}
                            </td>
                            <td className="max-w-[280px] px-4 py-3.5">
                              <span className="line-clamp-1 font-semibold text-foreground">{leadTitle(l)}</span>
                              {l.company_name && (
                                <span className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{l.company_name}</span>
                              )}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5">
                              {l.location ? (
                                <span className="inline-flex items-center gap-1 text-muted-foreground">
                                  <MapPin className="h-3.5 w-3.5 shrink-0" />
                                  {l.location}
                                </span>
                              ) : (
                                <span className="text-muted-foreground/50">—</span>
                              )}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5">
                              <span className="inline-flex items-center gap-1 rounded-full bg-primary/5 px-2 py-0.5 text-[0.7rem] font-medium text-primary">
                                <Users className="h-3 w-3" /> {l.interest_count}
                              </span>
                            </td>
                            <td className="px-4 py-3.5">
                              <ComingSoonBadge />
                            </td>
                            <td className="px-4 py-3.5">
                              {canEditLead(l) && (
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  title={l.image_url ? "Regenerate image" : "Generate image"}
                                  disabled={generatingId === l.id}
                                  onClick={() => handleGenerateImage(l.id)}
                                >
                                  {generatingId === l.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : l.image_url ? (
                                    <ImageIcon className="h-4 w-4" />
                                  ) : (
                                    <Sparkles className="h-4 w-4" />
                                  )}
                                </Button>
                                <Button variant="ghost" size="icon" title="Edit lead" onClick={() => openEdit(l)}><Pencil className="h-4 w-4" /></Button>
                                <Button variant="ghost" size="icon" title="Delete lead" className="text-destructive hover:text-destructive" onClick={() => handleDeleteLead(l.id)}><Trash2 className="h-4 w-4" /></Button>
                              </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <Pagination
                page={leadsPage}
                totalElements={leadsTotal}
                pageSize={PAGE_SIZE}
                onChange={(p) => setLeadsPage(p)}
                className="mt-6"
              />
            </>
          )}
        </PagePanel>
      )}

      {/* ── Interests Tab ── */}
      {activeTab === "interests" && (
        <PagePanel>
          <div className="mb-4">
            <PanelHeading title="Buyer Interests & Advisor Assignments" />
          </div>

          {/* Search */}
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <input
                className="w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm"
                placeholder="Search by buyer name, email, or lead title…"
                value={interestsSearchInput}
                onChange={(e) => setInterestsSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitInterestsSearch()}
              />
            </div>
            <Button size="sm" variant="outline" onClick={submitInterestsSearch}>Search</Button>
            {interestsSearch && (
              <Button size="sm" variant="ghost" onClick={() => { setInterestsSearchInput(""); setInterestsSearch(""); setInterestsPage(1); }}>
                <X className="h-4 w-4 mr-1" /> Clear
              </Button>
            )}
          </div>

          {interestsLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : interests.length === 0 ? (
            <EmptyPanel icon={Users} title="No buyer interests yet" description={interestsSearch ? "No interests match your search." : "Interests appear here when buyers express interest in a coming-soon lead."} />
          ) : (
            <>
              <div className="space-y-2.5">
                {interests.map((i) => (
                  <div key={i.id} className="rounded-xl border border-border bg-background p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOR[i.assignment_status] ?? "bg-slate-100 text-slate-600"}`}>
                            {i.assignment_status}
                          </span>
                          <button
                            className="text-sm font-semibold text-foreground hover:text-primary hover:underline text-left"
                            onClick={() => openViewLead(i.pipeline_id)}
                          >
                            {i.pipeline_title}
                          </button>
                          {i.pipeline_reference_code && (
                            <span className="font-mono text-[0.7rem] text-muted-foreground">{i.pipeline_reference_code}</span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Buyer: <span className="font-medium text-foreground">{i.buyer.first_name} {i.buyer.last_name}</span> · {i.buyer.email}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Advisor: {i.assigned_advisor ? (
                            <span className="font-medium text-foreground">{i.assigned_advisor.first_name} {i.assigned_advisor.last_name}</span>
                          ) : <span className="text-amber-600 font-medium">Unassigned</span>}
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button
                          size="sm" variant="outline"
                          onClick={() => { setReassignModal({ interestId: i.id, currentAdvisor: i.assigned_advisor?.id ?? null }); setReassignAdvisorId(""); setReassignNote(""); setAdvisorSearch(""); setAdvisorDropdownOpen(false); }}
                        >
                          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reassign
                        </Button>
                        <button
                          className="text-muted-foreground hover:text-foreground"
                          onClick={() => setExpandedInterest(expandedInterest === i.id ? null : i.id)}
                        >
                          {expandedInterest === i.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>

                    {expandedInterest === i.id && (
                      <div className="mt-3 ml-1 space-y-2">
                        {i.buyer_message && (
                          <div className="p-3 bg-muted/40 rounded-lg text-sm text-foreground">
                            <span className="font-medium text-xs text-muted-foreground uppercase tracking-wide block mb-1">Buyer Message</span>
                            {i.buyer_message}
                          </div>
                        )}
                        {i.assignment_history.length > 0 && (
                          <div>
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Assignment History</p>
                            <div className="space-y-1">
                              {i.assignment_history.map((h) => (
                                <div key={h.id} className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <Clock className="h-3 w-3 shrink-0" />
                                  <span><span className="font-medium text-foreground">{h.advisor_name}</span> — {h.action}</span>
                                  {h.note && <span className="text-slate-400 italic">"{h.note}"</span>}
                                  <span className="ml-auto">{new Date(h.created_at).toLocaleString()}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <Pagination
                page={interestsPage}
                totalElements={interestsTotal}
                pageSize={PAGE_SIZE}
                onChange={(p) => setInterestsPage(p)}
                className="mt-4"
              />
            </>
          )}
        </PagePanel>
      )}

      {/* ── Config Tab ── */}
      {activeTab === "config" && !orgAdmin && (
        <PagePanel>
          <div className="mb-4">
            <PanelHeading title="Settings" />
          </div>
          <div className="max-w-md space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground block mb-1">
                Advisor Active Threshold (days)
              </label>
              <p className="text-xs text-muted-foreground mb-2">
                Advisors who have NOT logged in within this many days are excluded from round-robin assignment.
              </p>
              <div className="flex gap-2">
                <input
                  type="number"
                  min={1}
                  value={configValue}
                  onChange={(e) => setConfigValue(e.target.value)}
                  className="w-24 rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
                <Button size="sm" onClick={handleSaveConfig} disabled={savingConfig}>
                  {savingConfig ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
                  Save
                </Button>
              </div>
            </div>
          </div>
        </PagePanel>
      )}

      {/* ── Lead Form Modal ── */}
      {showLeadForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowLeadForm(false)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92dvh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-border shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                  {editingLead ? <Pencil className="h-5 w-5 text-primary" /> : <Rocket className="h-5 w-5 text-primary" />}
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-bold text-foreground truncate">{editingLead ? "Edit Lead" : "New Coming Soon Lead"}</h2>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">{editingLead ? "Update the details for this coming soon listing." : "Fill in the details to create a new coming soon listing."}</p>
                </div>
              </div>
              <button
                onClick={() => setShowLeadForm(false)}
                className="ml-2 shrink-0 rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto px-6 py-5 space-y-5">
              {/* Business Identity */}
              <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
                <p className="text-[0.68rem] font-bold uppercase tracking-[0.18em] text-primary/70 mb-3 flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />Business Identity</p>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-foreground mb-1 block">Title</label>
                    <input
                      className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
                      value={form.title}
                      onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                      placeholder="e.g. HVAC Business, Southeast US"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-foreground mb-1 block">Company Name</label>
                      <input
                        className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
                        value={form.company_name}
                        onChange={(e) => setForm((p) => ({ ...p, company_name: e.target.value }))}
                        placeholder="e.g. Acme HVAC LLC"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-foreground mb-1 block">Owner Email</label>
                      <input
                        type="email"
                        className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
                        value={form.owner_email}
                        onChange={(e) => setForm((p) => ({ ...p, owner_email: e.target.value }))}
                        placeholder="owner@example.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-foreground mb-1 block">Website</label>
                    <input
                      type="url"
                      className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
                      value={form.website_url}
                      onChange={(e) => setForm((p) => ({ ...p, website_url: e.target.value }))}
                      placeholder="https://example.com"
                    />
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
                <p className="text-[0.68rem] font-bold uppercase tracking-[0.18em] text-primary/70 mb-3 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />Location</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-foreground mb-1 block">City</label>
                    <input
                      className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
                      value={form.city_raw}
                      onChange={(e) => setForm((p) => ({ ...p, city_raw: e.target.value }))}
                      placeholder="e.g. Atlanta"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-foreground mb-1 block">State</label>
                    <input
                      className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
                      value={form.state_raw}
                      onChange={(e) => setForm((p) => ({ ...p, state_raw: e.target.value }))}
                      placeholder="e.g. GA"
                    />
                  </div>
                </div>
              </div>

              {/* Classification */}
              <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
                <p className="text-[0.68rem] font-bold uppercase tracking-[0.18em] text-primary/70 mb-3 flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" />Classification</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-foreground mb-1 block">Industry Category</label>
                    <select
                      className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
                      value={form.industry_category_id}
                      onChange={(e) => setForm((p) => ({ ...p, industry_category_id: e.target.value }))}
                    >
                      <option value="">— None —</option>
                      {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-foreground mb-1 block">Potential Category</label>
                    <input
                      className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
                      value={form.potential_category}
                      onChange={(e) => setForm((p) => ({ ...p, potential_category: e.target.value }))}
                      placeholder="e.g. High potential"
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
                <p className="text-[0.68rem] font-bold uppercase tracking-[0.18em] text-primary/70 mb-3 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" />Description</p>
                <textarea
                  rows={4}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition resize-none"
                  value={form.business_description}
                  onChange={(e) => setForm((p) => ({ ...p, business_description: e.target.value }))}
                  placeholder="Brief overview of the business…"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border bg-muted/30 shrink-0">
              <Button variant="outline" onClick={() => setShowLeadForm(false)}>Cancel</Button>
              <Button onClick={handleSaveLead} disabled={saving} className="min-w-[90px]">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                {editingLead ? "Save Changes" : "Create Lead"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reassign Modal ── */}
      {reassignModal && (() => {
        const selectedAdvisor = advisors.find((a) => a.id === reassignAdvisorId) ?? null;
        const filtered = advisors.filter((a) => {
          const q = advisorSearch.toLowerCase();
          return !q || `${a.first_name} ${a.last_name}`.toLowerCase().includes(q);
        });
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setReassignModal(null)}>
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>

              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                <div>
                  <h2 className="text-base font-bold text-foreground">Reassign Advisor</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">Choose an advisor to take over this interest.</p>
                </div>
                <button
                  onClick={() => setReassignModal(null)}
                  className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-5 space-y-4">

                {/* Advisor searchable picker */}
                <div>
                  <label className="text-xs font-semibold text-foreground uppercase tracking-wide mb-2 block">Select Advisor</label>

                  {/* Selected chip or search input */}
                  {selectedAdvisor ? (
                    <div className="flex items-center gap-3 rounded-xl border border-primary/40 bg-primary/5 px-4 py-3">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="text-xs font-bold text-primary">
                          {selectedAdvisor.first_name[0]}{selectedAdvisor.last_name[0]}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground">{selectedAdvisor.first_name} {selectedAdvisor.last_name}</p>
                        <p className="text-xs text-muted-foreground">BL Advisor</p>
                      </div>
                      <button
                        onClick={() => { setReassignAdvisorId(""); setAdvisorSearch(""); }}
                        className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                        title="Change advisor"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                      <input
                        autoFocus
                        className="w-full rounded-xl border border-input bg-background pl-9 pr-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
                        placeholder="Search by name…"
                        value={advisorSearch}
                        onChange={(e) => { setAdvisorSearch(e.target.value); setAdvisorDropdownOpen(true); }}
                        onFocus={() => setAdvisorDropdownOpen(true)}
                      />
                      {advisorDropdownOpen && (
                        <div className="absolute z-10 mt-1 w-full rounded-xl border border-border bg-white shadow-lg overflow-hidden">
                          {filtered.length === 0 ? (
                            <div className="px-4 py-3 text-sm text-muted-foreground">No advisors found</div>
                          ) : (
                            <ul className="max-h-48 overflow-y-auto divide-y divide-border/50">
                              {filtered.map((a) => (
                                <li key={a.id}>
                                  <button
                                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted/60 transition-colors text-left"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => { setReassignAdvisorId(a.id); setAdvisorSearch(""); setAdvisorDropdownOpen(false); }}
                                  >
                                    <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                      <span className="text-xs font-bold text-primary">{a.first_name[0]}{a.last_name[0]}</span>
                                    </div>
                                    <span className="text-sm text-foreground">{a.first_name} {a.last_name}</span>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Note */}
                <div>
                  <label className="text-xs font-semibold text-foreground uppercase tracking-wide mb-2 block">Note <span className="normal-case font-normal text-muted-foreground">(optional)</span></label>
                  <textarea
                    rows={2}
                    className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition resize-none"
                    value={reassignNote}
                    onChange={(e) => setReassignNote(e.target.value)}
                    placeholder="Reason for reassignment…"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border bg-muted/30">
                <Button variant="outline" onClick={() => setReassignModal(null)}>Cancel</Button>
                <Button onClick={handleReassign} disabled={!reassignAdvisorId}>
                  <UserCheck className="h-4 w-4 mr-1" /> Reassign
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Lead Detail Modal (from interests tab) ── */}
      {(viewLead !== null || viewLeadLoading) && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={closeViewLead}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div
            className="relative w-full sm:max-w-2xl bg-white sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden max-h-[92dvh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-9 w-9 shrink-0 flex items-center justify-center rounded-xl bg-primary/10">
                  <Building2 className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-sm font-bold text-foreground truncate">
                    {viewLead?.title || viewLead?.company_name || "Lead Details"}
                  </h2>
                  <p className="text-xs text-muted-foreground">Coming Soon Lead</p>
                </div>
              </div>
              <button onClick={closeViewLead} className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted transition-colors shrink-0">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto">
              {viewLeadLoading ? (
                <div className="flex justify-center py-16">
                  <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
                </div>
              ) : viewLead ? (
                <>
                  {/* Cover image */}
                  <div className="relative h-52 w-full bg-gradient-to-br from-slate-100 to-slate-200 shrink-0">
                    {viewLead.image_url ? (
                      <img src={viewLead.image_url} alt={viewLead.title ?? ""} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center">
                        <Building2 className="h-14 w-14 text-slate-300" />
                      </div>
                    )}
                    {(viewLead.industry_category || viewLead.potential_category) && (
                      <div className="absolute left-3 top-3 flex flex-col gap-1">
                        {viewLead.industry_category && (
                          <span className="inline-flex items-center rounded-full border border-primary/20 bg-white/90 px-2.5 py-1 text-[0.62rem] font-semibold uppercase tracking-[0.1em] text-primary backdrop-blur-sm">
                            {viewLead.industry_category}
                          </span>
                        )}
                        {viewLead.potential_category && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-primary/25 bg-white/90 px-2.5 py-1 text-[0.62rem] font-semibold uppercase tracking-[0.1em] text-primary backdrop-blur-sm">
                            <Tag className="h-2.5 w-2.5" />{viewLead.potential_category}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="absolute right-3 top-3">
                      <span className="inline-flex items-center gap-1 rounded-full bg-[linear-gradient(135deg,hsl(28_92%_55%),hsl(15_88%_52%))] px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-[0.12em] text-white shadow-sm">
                        <Sparkles className="h-3 w-3" />Coming Soon
                      </span>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="px-5 py-4 space-y-3">
                    <div>
                      <h3 className="text-base font-extrabold text-foreground leading-snug">
                        {viewLead.title || viewLead.company_name || "Off-market opportunity"}
                      </h3>
                      {viewLead.reference_code && (
                        <p className="mt-0.5 font-mono text-xs text-muted-foreground">{viewLead.reference_code}</p>
                      )}
                      {viewLead.business_description && (
                        <p className="mt-1.5 text-sm text-muted-foreground leading-6">{viewLead.business_description}</p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
                      {viewLead.location && (
                        <span className="flex items-center gap-1.5"><MapPin className="h-3 w-3 shrink-0" />{viewLead.location}</span>
                      )}
                      {viewLead.owner_email && (
                        <span className="flex items-center gap-1.5"><Mail className="h-3 w-3 shrink-0" />{viewLead.owner_email}</span>
                      )}
                    </div>

                    {/* Admin action buttons */}
                    {canEditLead(viewLead) && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button
                        size="sm" variant="outline"
                        disabled={generatingId === viewLead.id}
                        onClick={() => handleGenerateImage(viewLead.id)}
                      >
                        {generatingId === viewLead.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                        ) : (
                          <Sparkles className="h-3.5 w-3.5 mr-1" />
                        )}
                        {viewLead.image_url ? "Regenerate Image" : "Generate Image"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => { closeViewLead(); openEdit(viewLead); }}>
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        className="text-destructive border-destructive/40 hover:bg-destructive/5"
                        onClick={() => { closeViewLead(); handleDeleteLead(viewLead.id); }}
                      >
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                      </Button>
                    </div>
                    )}
                  </div>

                  {/* Publish checklist */}
                  {viewLeadMode === "publish" && (
                    <div className="border-t border-border px-5 py-4 space-y-3 bg-muted/10">
                      <div className="flex items-center gap-2">
                        <ClipboardCheck className="h-4 w-4 text-primary" />
                        <p className="text-sm font-bold text-foreground">Pre-Publish Checklist</p>
                      </div>
                      {([
                        { key: "owner_connected", label: "Have you connected with the owner?" },
                        { key: "due_diligence_completed", label: "Have you completed all due diligence?" },
                        { key: "nda_signed", label: "Is the NDA signed with the owner?" },
                      ] as const).map((q) => (
                        <div key={q.key} className="flex items-center justify-between gap-3 rounded-xl border border-border/70 px-3 py-2.5 bg-white">
                          <span className="text-xs text-foreground">{q.label}</span>
                          <div className="flex shrink-0 gap-1.5">
                            {([{ val: true, text: "Yes" }, { val: false, text: "No" }] as const).map((opt) => {
                              const active = publishChecklist[q.key] === opt.val;
                              return (
                                <button
                                  key={opt.text}
                                  type="button"
                                  onClick={() => setPublishChecklist((p) => ({ ...p, [q.key]: opt.val }))}
                                  className={`rounded-lg px-3 py-1 text-xs font-semibold transition-colors ${
                                    active
                                      ? opt.val ? "bg-green-600 text-white" : "bg-rose-600 text-white"
                                      : "bg-muted text-muted-foreground hover:bg-muted/70"
                                  }`}
                                >
                                  {opt.text}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                      <textarea
                        rows={2}
                        className="w-full resize-none rounded-xl border border-input bg-white px-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition"
                        value={publishNotes}
                        onChange={(e) => setPublishNotes(e.target.value)}
                        placeholder="Additional notes (optional)…"
                      />
                      {!allPublishYes && (
                        <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-600">
                          All three items must be confirmed <span className="font-bold">Yes</span> before you can publish.
                        </p>
                      )}
                      <div className="flex items-center justify-end gap-3">
                        <Button variant="outline" onClick={() => setViewLeadMode("detail")}>Back</Button>
                        <Button onClick={confirmPublishFromView} disabled={!allPublishYes}>
                          <Upload className="h-4 w-4 mr-1.5" /> Continue to Listing
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </div>

            {/* Footer with Publish button (only in detail mode) */}
            {viewLead && viewLeadMode === "detail" && canEditLead(viewLead) && (
              <div className="border-t border-border bg-white px-5 py-3 flex justify-end shrink-0">
                <Button
                  onClick={() => {
                    setPublishChecklist({ owner_connected: null, due_diligence_completed: null, nda_signed: null });
                    setPublishNotes("");
                    setViewLeadMode("publish");
                  }}
                >
                  <Upload className="h-4 w-4 mr-1.5" /> Publish as Listing
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </PageShell>
  );
}
